import { useState, useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { apiClient } from '../../services/api'
import { useToast } from '../shared/Toast'
import { Modal } from '../shared/Modal'

interface WizardUser {
  rowKey: string
  email: string
  displayName: string
  apartmentNumber: string
  phone: string
  parkingSpaceNumber?: string
}

interface ParkingSpace {
  rowKey: string
  spaceNumber: string
  description: string
  activeContract: {
    contractId: string
    userName: string
    apartmentNumber: string
    isPrimary: boolean
  } | null
}

interface Props {
  user: WizardUser
  onClose: () => void
  onComplete: () => void
}

type Step = 'review' | 'configure' | 'confirm'

export function ActivationWizard({ user, onClose, onComplete }: Props) {
  const { t } = useTranslation()
  const { showToast } = useToast()

  const [step, setStep] = useState<Step>('review')
  const [parkingSpaces, setParkingSpaces] = useState<ParkingSpace[]>([])
  const [loading, setLoading] = useState(true)
  const [submitting, setSubmitting] = useState(false)

  // Contract form state
  const [selectedParkingId, setSelectedParkingId] = useState('')
  const [apartmentNumber, setApartmentNumber] = useState(user.apartmentNumber)
  const [startDate, setStartDate] = useState(() => {
    const now = new Date()
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-01`
  })

  // Parking match status
  const [parkingStatus, setParkingStatus] = useState<
    { type: 'valid'; space: ParkingSpace } |
    { type: 'taken'; space: ParkingSpace } |
    { type: 'not_found'; requestedNumber: string } |
    { type: 'none' }
  >({ type: 'none' })

  useEffect(() => {
    setLoading(true)
    apiClient.get<{ items: ParkingSpace[] }>('/api/manage/parking')
      .then(res => {
        setParkingSpaces(res.items)

        const requested = user.parkingSpaceNumber?.trim()
        if (!requested) {
          setParkingStatus({ type: 'none' })
          return
        }

        const match = res.items.find(p => String(p.spaceNumber) === requested)
        if (match && !match.activeContract) {
          setParkingStatus({ type: 'valid', space: match })
          setSelectedParkingId(match.rowKey as string)
        } else if (match && match.activeContract) {
          setParkingStatus({ type: 'taken', space: match })
          setSelectedParkingId(match.rowKey as string)
        } else {
          setParkingStatus({ type: 'not_found', requestedNumber: requested })
        }
      })
      .finally(() => setLoading(false))
  }, [user.parkingSpaceNumber])

  const selectedSpace = parkingSpaces.find(p => (p.rowKey as string) === selectedParkingId)

  async function handleReject() {
    if (!window.confirm(t('admin.wizard.rejectConfirm'))) return
    setSubmitting(true)
    try {
      await apiClient.post(`/api/manage/users/${user.rowKey}/reject`)
      showToast(t('admin.wizard.rejected'), 'success')
      onComplete()
      onClose()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  async function handleSubmit() {
    setSubmitting(true)
    try {
      // 1. Activate user (with contract info for email)
      const activateRes = await apiClient.post<{ ok: boolean; warning?: string }>(
        `/api/manage/users/${user.rowKey}/activate`,
        {
          parkingNumber: selectedSpace?.spaceNumber ?? '',
          apartmentNumber,
          startDate,
        }
      )
      if (activateRes.warning) {
        showToast(activateRes.warning, 'warning')
      }

      // 2. Create contract
      await apiClient.post('/api/manage/contracts', {
        userId: user.rowKey,
        parkingSpaceId: selectedParkingId,
        apartmentNumber,
        startDate,
      })

      showToast(t('admin.wizard.success'), 'success')
      onComplete()
    } catch {
      showToast(t('errors.generic'), 'error')
    } finally {
      setSubmitting(false)
    }
  }

  const canProceedToConfigure = true
  const canProceedToConfirm = selectedParkingId && apartmentNumber && startDate

  return (
    <Modal open={true} onClose={onClose} title={t('admin.wizard.title')}>
      {loading ? (
        <p className="text-brand-400 py-4">{t('admin.loading')}</p>
      ) : (
        <div className="space-y-6">
          {/* Step indicators */}
          <div className="flex gap-1">
            {(['review', 'configure', 'confirm'] as Step[]).map((s, i) => (
              <div key={s} className={`h-1 flex-1 rounded-full ${
                step === s ? 'bg-brand-500' :
                (['review', 'configure', 'confirm'].indexOf(step) > i) ? 'bg-brand-300' :
                'bg-brand-100'
              }`} />
            ))}
          </div>

          {/* Step 1: Review user info */}
          {step === 'review' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-brand-700">{t('admin.wizard.userInfo')}</h3>
              <div className="bg-brand-50 rounded-lg p-4 space-y-2 text-sm">
                <Row label={t('admin.users.name')} value={user.displayName} />
                <Row label={t('admin.users.email')} value={user.email} />
                <Row label={t('admin.users.phone')} value={user.phone} />
                <Row label={t('admin.users.apartment')} value={user.apartmentNumber} />
                <div className="pt-2 border-t border-brand-100">
                  <Row
                    label={t('admin.wizard.requestedParking')}
                    value={user.parkingSpaceNumber || t('admin.wizard.noParking')}
                  />
                  <ParkingStatusBadge status={parkingStatus} t={t} />
                </div>
              </div>
              <div className="flex justify-between">
                <button
                  onClick={handleReject}
                  disabled={submitting}
                  className="text-red-600 text-sm px-5 py-2 rounded-lg hover:bg-red-50 transition-colors"
                >
                  {t('admin.wizard.reject')}
                </button>
                <button
                  onClick={() => setStep('configure')}
                  disabled={!canProceedToConfigure}
                  className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
                >
                  {t('admin.wizard.configureContract')} &rarr;
                </button>
              </div>
            </div>
          )}

          {/* Step 2: Configure contract */}
          {step === 'configure' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-brand-700">{t('admin.wizard.configureContract')}</h3>

              <div>
                <label className="block text-xs text-brand-500 mb-1">{t('admin.contracts.parkingSpace')}</label>
                <select
                  value={selectedParkingId}
                  onChange={e => setSelectedParkingId(e.target.value)}
                  className="w-full rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500"
                >
                  <option value="">--</option>
                  {parkingSpaces.map(p => (
                    <option key={p.rowKey as string} value={p.rowKey as string}>
                      {p.spaceNumber}
                      {p.activeContract ? ` (${p.activeContract.userName})` : ''}
                    </option>
                  ))}
                </select>
                {selectedSpace?.activeContract && (
                  <p className="text-amber-600 text-xs mt-1">
                    {t('admin.wizard.parkingTaken')}: {selectedSpace.activeContract.userName}
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs text-brand-500 mb-1">{t('admin.users.apartment')}</label>
                <input
                  type="text"
                  value={apartmentNumber}
                  onChange={e => setApartmentNumber(e.target.value)}
                  className="w-full rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500"
                />
              </div>

              <div>
                <label className="block text-xs text-brand-500 mb-1">{t('admin.contracts.startDate')}</label>
                <input
                  type="date"
                  value={startDate}
                  onChange={e => setStartDate(e.target.value)}
                  className="w-full rounded-lg border-brand-200 text-sm focus:border-brand-500 focus:ring-brand-500"
                />
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep('review')}
                  className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors"
                >
                  &larr; {t('admin.users.cancel')}
                </button>
                <button
                  onClick={() => setStep('confirm')}
                  disabled={!canProceedToConfirm}
                  className="bg-brand-500 text-white text-sm px-5 py-2 rounded-lg hover:bg-brand-600 disabled:opacity-50 transition-colors"
                >
                  {t('admin.wizard.confirm')} &rarr;
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Confirm & activate */}
          {step === 'confirm' && (
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-brand-700">{t('admin.wizard.confirm')}</h3>
              <div className="bg-green-50 border border-green-200 rounded-lg p-4 space-y-2 text-sm">
                <Row label={t('admin.users.name')} value={user.displayName} />
                <Row label={t('admin.users.email')} value={user.email} />
                <Row label={t('admin.contracts.parkingSpace')} value={selectedSpace?.spaceNumber ?? '-'} />
                <Row label={t('admin.users.apartment')} value={apartmentNumber} />
                <Row label={t('admin.contracts.startDate')} value={startDate} />
              </div>

              <div className="flex justify-between">
                <button
                  onClick={() => setStep('configure')}
                  className="text-brand-500 text-sm px-5 py-2 rounded-lg hover:bg-brand-50 transition-colors"
                >
                  &larr; {t('admin.users.cancel')}
                </button>
                <button
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-green-600 text-white text-sm px-5 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 transition-colors"
                >
                  {submitting ? t('admin.wizard.activating') : t('admin.wizard.confirm')}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </Modal>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex justify-between">
      <span className="text-brand-500">{label}</span>
      <span className="text-brand-700 font-medium">{value}</span>
    </div>
  )
}

function ParkingStatusBadge({ status, t }: {
  status: { type: string; space?: ParkingSpace; requestedNumber?: string }
  t: (key: string) => string
}) {
  if (status.type === 'none') return null

  if (status.type === 'valid') {
    return (
      <p className="text-green-600 text-xs mt-1 flex items-center gap-1">
        <span>&#10003;</span> {t('admin.wizard.parkingValid')}
      </p>
    )
  }

  if (status.type === 'taken') {
    const space = status.space as ParkingSpace
    return (
      <p className="text-amber-600 text-xs mt-1">
        &#9888; {t('admin.wizard.parkingTaken')}: {space.activeContract?.userName}
      </p>
    )
  }

  if (status.type === 'not_found') {
    return (
      <p className="text-amber-600 text-xs mt-1">
        &#9888; {t('admin.wizard.parkingNotFound')}: {status.requestedNumber}
      </p>
    )
  }

  return null
}
